import { MouseEvent } from 'react';

import classNames from 'classnames';

import { Button, Spinner } from '@uikit';

import { useAppQuery } from '@query';

import { DeserializedAppData, DeserializedStorePageApp } from '@api';

import { ACTION_TYPES, CommonStory } from '@shared/api/types/story';
import { useBreakpoint } from '@shared/hooks/useBreakpoint';

import StoreAppCard from '@store:web/components/common/AppCard/StoreAppCard';

function normalizeAppDataForStoreCard(app: DeserializedAppData): DeserializedStorePageApp {
  return {
    id: app.id,
    type: app.type,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    attributes: {
      ...app.attributes,
      appChain: app?.attributes?.chainName,
      icon: app?.attributes?.appIcon,
      appId: app?.attributes?.appLink,
      description: app?.attributes?.shortDescription,
      externalLink: app?.attributes?.appWebsite,
      ...(app?.attributes?.appBanner ? { icon: app?.attributes?.appBanner } : {}),
      name: app?.attributes?.appName,
      subcategory: app?.attributes?.subCategory,
    },
    hotOffers: app?.hotOffers,
  };
}

export default function StoryMainInfoBlock({
  story,
  onButtonClick,
}: {
  story: CommonStory;
  onButtonClick: (type?: string, event?: MouseEvent, internalRoute?: string) => void;
}) {
  const {
    type,
    buttonText = 'Click',
    title,
    description,
    appId = '',
    bigText,
    internalRoute,
    externalRoute,
  } = story?.content ?? {};
  const isLargeTablet = useBreakpoint('lg', 'max');
  const { data: app, isPending: isAppLoading } = useAppQuery(appId, { enabled: !!appId });

  const linkProps: Record<string, string | undefined> = {
    as: 'a',
    href: externalRoute || internalRoute,
    target: '_blank',
    rel: ' noopener noreferrer',
  };

  if (type === ACTION_TYPES.APP && isAppLoading && appId) {
    return <Spinner />;
  }

  if (bigText) {
    return <h2 className="stories-big-text">{bigText}</h2>;
  }

  const cardNormalizedApp = app ? normalizeAppDataForStoreCard(app) : app;

  if (appId) {
    return (
      <div className="stories-control-block">
        {isAppLoading && <Spinner className="mx-auto" />}
        {!isAppLoading && cardNormalizedApp && (
          <StoreAppCard app={cardNormalizedApp} className="w-full" />
        )}
        {!isAppLoading && !cardNormalizedApp && (
          <p className="stories-description mb-0">App not found</p>
        )}
      </div>
    );
  }

  if (title || description) {
    return (
      <div className="stories-control-block">
        {(title || description) && (
          <div>
            {title && <h3 className="stories-title">{title}</h3>}
            {description && <p className="stories-description">{description}</p>}
          </div>
        )}

        <Button
          variant="secondary"
          onClick={(event) =>
            onButtonClick(undefined, event as unknown as MouseEvent, internalRoute)
          }
          className={classNames('whitespace-nowrap text-lg h-[50px]', { 'w-full': isLargeTablet })}
          {...linkProps}
        >
          {buttonText || 'Click me'}
        </Button>
      </div>
    );
  }

  return null;
}
